import { Box } from '@material-ui/core';
import Konva from 'konva';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Layer, Rect, Stage } from 'react-konva';
import useImage from 'use-image';

import { useRestaurant } from '../../../../selectors/RestaurantSelectors';
import personIcon from '../../../shared/assets/icons/person.svg';
import { SchemaTable } from './schemaTable';

const tableDefaults = {
  type: 'LIST_TABLE' as const,
  width: 70,
  height: 70,
  z: 0,
  angle: 0,
};

const tableSpacing = 24;
const containerSpacing = 32;
const tableWidthWithArtefacts = tableDefaults.width + 2;
const tableHeightWithArtefacts = tableDefaults.height + 2;

export const TableGridWidget = ({
  section,
  selectedTableIds = [],
  onClickSchema,
  onClickTable,
  tablesSchedule,
}: {
  section: GM.ReservesSchemas.SectionDTO;
  selectedTableIds?: number[];
  onClickSchema?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  onClickTable: (id: number) => void;
  tablesSchedule: GM.ReservesSchemas.TablesSchedule;
}) => {
  const schemaContainerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 300, height: 300 });

  const updateSize = useCallback(() => {
    if (!schemaContainerRef.current) {
      return;
    }

    // Используем getBoundingClientRect для получения реальных размеров контейнера
    const rect = schemaContainerRef.current.getBoundingClientRect();

    // Используем размеры самого контейнера, а не window
    // Это гарантирует, что канвас занимает ровно доступную область родителя
    const availableWidth = rect.width;
    const availableHeight = rect.height;

    // setSize({
    //   width: availableWidth,
    //   height: availableHeight,
    // });

    // Используем requestAnimationFrame для предотвращения ResizeObserver loop
    requestAnimationFrame(() => {
      setSize({
        width: availableWidth,
        height: availableHeight,
      });
    });
  }, [schemaContainerRef]);

  const gridTables = useMemo(() => {
    if (!section?.tables || section.tables.length === 0) {
      return [];
    }

    const tablesSorted = [...section.tables].sort((a, b) => {
      const an = a.number ?? '';
      const bn = b.number ?? '';
      return an.localeCompare(bn);
    });

    let row = 0;
    let col = 0;

    return tablesSorted.map((tableDto) => {
      let x = col * (tableWidthWithArtefacts + tableSpacing) + containerSpacing;
      if (col > 0 && x + tableWidthWithArtefacts + containerSpacing > size.width) {
        col = 0;
        x = col * (tableWidthWithArtefacts + tableSpacing) + containerSpacing;
        row += 1;
      }

      const t: GM.ReservesSchemas.SchemaListTable = {
        ...tableDefaults,
        table: tableDto,
        id: tableDto.id,
        x,
        y: row * (tableHeightWithArtefacts + tableSpacing) + containerSpacing,
      };

      col += 1;

      return t;
    });
  }, [section, size.width]);

  useEffect(() => {
    if (!schemaContainerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(schemaContainerRef.current);

    // Первоначальный расчет размеров
    updateSize();

    // Также слушаем resize окна на всякий случай
    window.addEventListener('resize', updateSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, [updateSize, section?.id]);

  const [personImage] = useImage(personIcon);

  const selectedTables = useMemo<GM.ReservesSchemas.SchemaListTable[]>(() => {
    return gridTables.filter((t) => selectedTableIds.includes(t.id));
  }, [gridTables, selectedTableIds]);

  const contentSize = useMemo(() => {
    let width = 0;
    let height = 0;

    gridTables.forEach((t) => {
      width = Math.max(t.x + tableWidthWithArtefacts + containerSpacing, width);
      height = Math.max(t.y + tableHeightWithArtefacts + containerSpacing, height);
    });

    return { width, height };
  }, [gridTables]);

  const restaurant = useRestaurant();
  const restaurantTz = restaurant?.info?.timeZoneOffset || undefined;

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      style={{ overflow: 'hidden', minHeight: 0, minWidth: 0 }}
    >
      <div
        ref={schemaContainerRef}
        style={{
          flexGrow: 1,
          flexShrink: 1,
          minHeight: 0,
          minWidth: 0,
          position: 'relative',
          paddingBottom: `${containerSpacing}px`,
          overflow: 'hidden',
        }}
      >
        <Stage
          width={contentSize.width}
          height={contentSize.height}
          onClick={onClickSchema}
          onTap={onClickSchema}
        >
          <Layer>
            <Rect
              width={contentSize.width}
              height={contentSize.height}
              listening={false}
              fill="#F8F8F8"
              cornerRadius={8}
            />
          </Layer>

          <Layer>
            {gridTables.map((t) => {
              if (selectedTableIds.includes(t.id)) {
                return null;
              }

              return (
                <SchemaTable
                  key={t.id}
                  schemaTable={t}
                  selectedTableIds={selectedTableIds}
                  onClick={onClickTable}
                  // dimmed={selectedTables.length > 0}
                  tableSchedule={tablesSchedule[t.table.id]}
                  personImage={personImage}
                  // scale={1}
                  restaurantTz={restaurantTz}
                />
              );
            })}
          </Layer>

          {selectedTables.length > 0 && (
            <Layer>
              <Rect
                width={contentSize.width}
                height={contentSize.height}
                listening={false}
                fill="rgba(0, 0, 0, 0.5)"
                cornerRadius={8}
              />

              {selectedTables.map((schemaTable) => (
                <SchemaTable
                  key={schemaTable.id}
                  schemaTable={schemaTable}
                  selectedTableIds={selectedTableIds}
                  onClick={onClickTable}
                  tableSchedule={tablesSchedule[schemaTable.table.id]}
                  personImage={personImage}
                  // scale={1}
                  restaurantTz={restaurantTz}
                />
              ))}
            </Layer>
          )}
        </Stage>
      </div>
    </Box>
  );
};
